import React, { useEffect, useState } from 'react';

import { formatDate } from '@betterment/core';
import { track, useQuery } from '@betterment/js-runtime';
import { Box, Flex, Tag, Text } from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import { FlashProvider } from '../../../Shared/contexts/FlashContext';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { DetailedTransaction, TransactionStatus } from '../../data';
import { getCashbackProperties, getDisplayStatus, parseDescription } from '../../models/detailed-transaction';
import { useAccount } from '../AccountProvider';
import { useCustomer } from '../CustomerProvider';
import TransactionIcon from '../TransactionIcon';
import TransactionModal from '../TransactionModal';
import TransactionAmount from '../TransactionModal/Amount';
import { useTransactions } from '../TransactionsContext';

const ClickableBox = styled(Flex)`
  cursor: pointer;
`;

const OverflowText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface Props extends DetailedTransaction {
  shouldDisplayBottomBorder?: boolean;
}

const DetailedTransactionRow: React.FC<React.PropsWithChildren<Props>> = ({
  shouldDisplayBottomBorder,
  ...transaction
}) => {
  const {
    id,
    status,
    createdAt,
    amountCents,
    isCredit,
    formattedDescription,
    category,
    earnedCashbackRewardsCents,
    pendingCashbackRewardsCents,
    icon,
    isDirty,
  } = transaction;
  const { customer } = useCustomer();
  const { account } = useAccount();
  const accountType = account.isJointAccount ? 'Joint' : 'Individual';
  const { description, accountNumber: transactionAccountNumber } = parseDescription(formattedDescription);

  const [shouldShowTransactionModal, setShowTransactionModal] = useState(false);

  const toggleTransactionModal = () => {
    setShowTransactionModal(!shouldShowTransactionModal);
    track('ElementClicked', { name: `TransactionDetails-${accountType}`, module: 'CheckingTransactionDetails' });
  };

  const handleKeyPress: React.KeyboardEventHandler = event => {
    if (['Enter', ' '].includes(event.key)) {
      toggleTransactionModal();
    }
  };

  const { cashbackStatus } = getCashbackProperties(customer, earnedCashbackRewardsCents, pendingCashbackRewardsCents);
  const displayStatus = getDisplayStatus(status);

  const { replaceTransaction } = useTransactions(null, { skip: true });
  const { refetch, cancel: cancelRefetchRequest } = useQuery<DetailedTransaction>(
    urlBuilder.apiV2CheckingDetailedTransaction.absolute({ accountId: account.id, transactionId: id }),
    { skip: true }
  );

  useEffect(() => {
    if (isDirty) {
      void refetch()
        .then(({ data: freshTransaction }) => {
          if (freshTransaction) replaceTransaction(freshTransaction);
        })
        .catch();
    }

    return cancelRefetchRequest;
  }, [isDirty, refetch, replaceTransaction, cancelRefetchRequest]);

  return (
    <ClickableBox
      as="tr"
      tabIndex={0}
      key={id}
      onClick={toggleTransactionModal}
      onKeyPress={handleKeyPress}
      data-testid={`detailed-transaction-row-${id}`}
      py="6"
      borderBottom={shouldDisplayBottomBorder ? '1px solid' : null}
      borderColor="bmtGrey20"
    >
      {shouldShowTransactionModal && (
        <FlashProvider>
          <TransactionModal {...transaction} closeModal={toggleTransactionModal} accountType={accountType} />
        </FlashProvider>
      )}
      <Flex as="td" alignItems="top" mr="auto">
        <Box mt="2">
          <TransactionIcon icon={icon} category={category} size="small" />
        </Box>
        <Flex ml="4" flexDirection="column" minWidth="0">
          <Flex>
            <OverflowText role="button" mr="1">
              {description}
            </OverflowText>
            <Text>{transactionAccountNumber}</Text>
          </Flex>
          <Text fontSize="1" color="bmtGrey50">
            {formatDate(createdAt)}
          </Text>
          {cashbackStatus && (
            <Box>
              <Tag mt="2" bg="bmtTeal10">
                <Text color="bmtTeal90">{`Cash back ${cashbackStatus}`}</Text>
              </Tag>
            </Box>
          )}
        </Flex>
      </Flex>
      <Flex as="td" pt={status !== TransactionStatus.Settled ? 0 : 3} flexDirection="column" alignItems="top">
        <TransactionAmount status={status} isCredit={isCredit} amountCents={amountCents} />
        {status !== TransactionStatus.Settled && (
          <Text fontSize="1" textAlign="right" color="bmtGrey50">
            {displayStatus}
          </Text>
        )}
      </Flex>
    </ClickableBox>
  );
};
DetailedTransactionRow.displayName = 'TransactionRow';

export default DetailedTransactionRow;
